// Shared HTML-email rendering — used by both the admin Recruitment page
// (SendEmailModal, building the actual email that gets sent) and the Email
// Templates management page (previewing what a template will look like
// before it's ever attached to a real candidate). Keeping this in one place
// means both previews are guaranteed to render identically.

// Escapes free-text before interpolating into an HTML string.
export function escapeHtml(value) {
  if (value === null || value === undefined || value === "") return "";
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// Splits the plain-text body on blank lines into <p> tags, with a minimal
// **bold** markdown pass — the plain textarea has no rich-text formatting,
// so this is the only way to bold specific words (e.g. the sign-off's
// team/company name) in the rendered email.
export function bodyTextToHtmlParagraphs(text) {
  const paragraphs = String(text || "").split(/\n{2,}/).filter((p) => p.trim() !== "");
  return paragraphs
    .map((p, i) => {
      const isLast = i === paragraphs.length - 1;
      const escaped = escapeHtml(p)
        .replace(/\n/g, "<br/>")
        .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
      return `<p style="margin:0 0 ${isLast ? 0 : 16}px;">${escaped}</p>`;
    })
    .join("");
}

// One distinct header color per template key (not just per stage) — each
// template gets its own hue so the admin/candidate can tell at a glance
// what kind of email this is, loosely grouped by sentiment (green = good
// news, red/orange/pink = rejection-family, amber/stone = pending,
// purple/teal/blue = neutral/informational).
export const EMAIL_TEMPLATE_COLORS = {
  APPLICATION_RECEIVED:      { bg: "#7c3aed", gradient: "linear-gradient(135deg,#a78bfa,#6d28d9)" }, // purple
  SCREENING_SHORTLISTED:     { bg: "#0d9488", gradient: "linear-gradient(135deg,#2dd4bf,#0f766e)" }, // teal
  SCREENING_NOT_SHORTLISTED: { bg: "#e11d48", gradient: "linear-gradient(135deg,#fb7185,#9f1239)" }, // rose
  // Test/Interview keys are stage-prefixed (see defaultTemplateKey in
  // PlacementAdminPage.js) — both variants of each "type" share the same
  // color so Scheduled/Passed/Failed/On-Hold read consistently regardless
  // of which stage sent it.
  TEST_SCHEDULED:            { bg: "#2563eb", gradient: "linear-gradient(135deg,#60a5fa,#1d4ed8)" }, // blue
  INTERVIEW_SCHEDULED:       { bg: "#2563eb", gradient: "linear-gradient(135deg,#60a5fa,#1d4ed8)" }, // blue
  TEST_RESULT_PASSED:        { bg: "#16a34a", gradient: "linear-gradient(135deg,#4ade80,#15803d)" }, // green
  INTERVIEW_RESULT_PASSED:   { bg: "#16a34a", gradient: "linear-gradient(135deg,#4ade80,#15803d)" }, // green
  TEST_RESULT_FAILED:        { bg: "#dc2626", gradient: "linear-gradient(135deg,#f87171,#b91c1c)" }, // red
  INTERVIEW_RESULT_FAILED:   { bg: "#dc2626", gradient: "linear-gradient(135deg,#f87171,#b91c1c)" }, // red
  TEST_RESULT_ON_HOLD:       { bg: "#d97706", gradient: "linear-gradient(135deg,#fbbf24,#b45309)" }, // amber
  INTERVIEW_RESULT_ON_HOLD:  { bg: "#d97706", gradient: "linear-gradient(135deg,#fbbf24,#b45309)" }, // amber
  FINAL_SELECTED:            { bg: "#ca8a04", gradient: "linear-gradient(135deg,#facc15,#a16207)" }, // gold
  FINAL_REJECTED:            { bg: "#c2410c", gradient: "linear-gradient(135deg,#fb923c,#9a3412)" }, // orange
  FINAL_ON_HOLD:             { bg: "#78716c", gradient: "linear-gradient(135deg,#a8a29e,#57534e)" }, // stone
  FINAL_OFFER_DECLINED:      { bg: "#475569", gradient: "linear-gradient(135deg,#94a3b8,#334155)" }, // slate
  FINAL_NOT_SHORTLISTED:     { bg: "#be185d", gradient: "linear-gradient(135deg,#f472b6,#831843)" }, // pink
};
export const DEFAULT_EMAIL_COLOR = { bg: "#4f46e5", gradient: "linear-gradient(135deg,#6366f1,#4338ca)" }; // indigo fallback

// Wraps a subject/body into the polished, table-based HTML email actually
// sent to candidates — inline styles throughout (email clients strip
// <style> blocks and ignore CSS variables), a branded header, a legible
// content area, and a standard "system generated / do not reply" footer.
export function buildEmailHtml({ subject, body, companyName, companyEmail, templateKey }) {
  const stageLabel = "Application Update"; // header badge — same wording on every template, regardless of stage
  const position = "Talent Acquisition";
  const senderName = companyName ? `${companyName}` : "Recruitment Team";
  const paragraphsHtml = bodyTextToHtmlParagraphs(body);
  const year = new Date().getFullYear();
  const { bg: headerBg, gradient: headerGradient } = EMAIL_TEMPLATE_COLORS[templateKey] || DEFAULT_EMAIL_COLOR;

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1.0" />
<meta http-equiv="X-UA-Compatible" content="IE=edge" />
<title>${escapeHtml(subject)}</title>
<style>
*{box-sizing:border-box;}
body{margin:0;}
* { scrollbar-width: thin; scrollbar-color: #cbd5e1 transparent; }
*::-webkit-scrollbar { width: 8px; height: 8px; }
*::-webkit-scrollbar-track { background: transparent; }
*::-webkit-scrollbar-thumb { background-color: #cbd5e1; border-radius: 8px; border: 2px solid transparent; background-clip: padding-box; }
*::-webkit-scrollbar-thumb:hover { background-color: #94a3b8; background-clip: padding-box; }
</style>
</head>
<body style="margin:0;padding:0;background-color:#f4f6f8;-webkit-text-size-adjust:100%;">
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:#f4f6f8;">
    <tr>
      <td align="center" style="padding:36px 16px;">
        <table role="presentation" width="600" cellpadding="0" cellspacing="0" border="0" style="max-width:600px;width:100%;background-color:#ffffff;border-radius:14px;overflow:hidden;box-shadow:0 4px 18px rgba(16,24,40,0.08);font-family:'Segoe UI',Helvetica,Arial,sans-serif;">

          <!-- header -->
          <tr>
            <td style="background-color:${headerBg};background-image:${headerGradient};padding:30px 36px;">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">
                <tr>
                  <td style="font-size:19px;font-weight:700;color:#ffffff;letter-spacing:.2px;">${escapeHtml(senderName)}</td>
                  <td align="right" style="white-space:nowrap;">
                    <span style="display:inline-block;font-size:11px;font-weight:700;color:#ffffff;background-color:rgba(255,255,255,0.18);border-radius:20px;padding:5px 12px;white-space:nowrap;">${escapeHtml(stageLabel.toUpperCase())}</span>
                  </td>
                </tr>
              </table>
              <div style="font-size:13px;color:rgba(255,255,255,0.85);margin-top:6px;">${escapeHtml(position)}</div>
            </td>
          </tr>

          <!-- greeting strip -->
          <tr>
            <td style="padding:28px 36px 0;">
              <div style="font-size:15px;font-weight:700;color:#101828;">${escapeHtml(subject)}</div>
            </td>
          </tr>

          <!-- body -->
          <tr>
            <td style="padding:16px 36px 32px;">
              <div style="font-size:14px;line-height:1.7;color:#344054;">
                ${paragraphsHtml}
              </div>
            </td>
          </tr>

          <!-- divider -->
          <tr>
            <td style="padding:0 36px;">
              <div style="border-top:1px solid #e4e8ee;"></div>
            </td>
          </tr>

          <!-- footer -->
          <tr>
            <td style="padding:22px 36px 30px;">
              <div style="font-size:11.5px;color:#98a2b3;text-align:center;line-height:1.7;">
                This is an automated email. Please do not reply to this message.<br/>
                ${companyEmail ? `For recruitment-related enquiries, please contact us at <a href="mailto:${escapeHtml(companyEmail)}" style="color:${headerBg};text-decoration:none;">${escapeHtml(companyEmail)}</a><br/>` : ""}
                &copy; ${year} ${escapeHtml(senderName)} &middot; All rights reserved.
              </div>
            </td>
          </tr>

        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
}

// Substitutes {{token}} placeholders in a raw template string with values
// from `vars` — tokens with no matching key are left as-is (so a typo in a
// template shows up visibly as literal "{{typo}}" text rather than
// silently vanishing).
export function renderTemplateText(raw, vars) {
  if (!raw) return "";
  return raw.replace(/\{\{(\w+)\}\}/g, (match, key) => (key in vars ? vars[key] : match));
}
